import { useEffect, useMemo, useReducer, useState } from "react";

const FILTER_TYPE = {
    BTN: 0,
    SELECT: 1,
    RANGE: 2,
    CHECK: 3
};

const FILTER_MODE = {
    REMOVE: -1,
    REPLACE: 0,
    ADD: 1,
    isValid: (mode) => mode >= FILTER_MODE.REMOVE && mode <= FILTER_MODE.ADD
};

export const ACTIVE_FILTERS_KEY = "cf_Active";
export const SORT_BY_KEY = "cf_sortBy";

const sortFunc = (a, b, direction = 1) => {
    // direction is either "1" or "-1", multiplying that with the item value sets the direction
    a = a === undefined ? 999999 : a * direction;
    b = b === undefined ? 999999 : b * direction;
    return a - b;
};

const sortOptionDefinitions = [
    {
        value: "position",
        labelTranslation: "camera.filter.sort.position",
        itemAccessor: (item) => item.position,
        func: sortFunc,
        direction: 1
    },
    {
        value: "category",
        labelTranslation: "camera.filter.sort.category",
        itemAccessor: (item) => item.categories[0]?.sortValue,
        func: sortFunc,
        direction: 1
    },
    {
        value: "series",
        labelTranslation: "camera.filter.sort.series",
        itemAccessor: (item) => item.series?.sortValue,
        func: sortFunc,
        direction: 1
    },
    {
        value: "imageformat",
        labelTranslation: "camera.filter.sort.imageformat",
        itemAccessor: (item) => item.imageFormat?.reduce((maxSortValue, { sortValue }) => {
            return Math.max(maxSortValue, sortValue);
        }, 0),
        func: sortFunc,
        direction: -1
    },
    {
        value: "thermalres",
        labelTranslation: "camera.filter.sort.thermalres",
        itemAccessor: (item) => item.thermalResolution?.sortValue,
        func: sortFunc,
        direction: 1
    },
    {
        value: "application",
        labelTranslation: "camera.filter.sort.application",
        itemAccessor: (item) => item.applications[0]?.filterLabel,
        func: (a, b) => a.localeCompare(b),
        direction: 1
    },
];

const filterFunc = ({ key, options = [] }, item, itemAccessor) => {
    const itemValues = itemAccessor(item);
    const activeValues = options
        .filter(({ active }) => active)
        .map(({ value }) => value);

    if (activeValues.length === 0) {
        return true;
    }

    return itemValues.some(({ filterValue }) =>
        activeValues.includes(filterValue)
    );
};

const imgFormatFilterFunc = (
    { key, options = [] },
    { imageFormat: formats }
) => {
    const activeValues = options
        .filter(({ active }) => active)
        .map(({ value }) => value);

    if (activeValues.length === 0) {
        return true;
    }

    return formats.some(({ filterValue }) =>
        activeValues.includes(filterValue)
    );
};

const frequencyFilterFunc = ({ key, options = [] }, item, itemAccessor) => {
    const itemValues = itemAccessor(item);

    const selectedValue = options.find(({ active }) => active);

    if (!selectedValue) {
        return true;
    }

    const activeValues = options
        .filter(({ position }) => position >= selectedValue.position)
        .map(({ value }) => value);

    if (activeValues.length === 0) {
        return true;
    }

    return itemValues.some(({ filterValue }) =>
        activeValues.includes(filterValue)
    );
};

const filterDefinitions = {
    category: {
        type: FILTER_TYPE.SELECT,
        settings: {
            labelTranslation: "camera.filter.cameracategory.label",
            emptyOptionLabelTranslation:
                "camera.filter.cameracategory.dropdownbutton"
        },
        func: filterFunc,
        itemAccessor: ({ categories = [] }) =>
            categories.map(({ filterValue, filterLabel, filterPosition }) => ({
                filterValue: filterValue.toString(),
                filterLabel: filterLabel.toString(),
                filterPosition
            })),
        filterOrder: 1
    },
    series: {
        type: FILTER_TYPE.CHECK,
        settings: {
            labelTranslation: "camera.filter.cameraseries.label"
        },
        func: filterFunc,
        itemAccessor: (item) => {
            const { filterValue, filterLabel, filterPosition } = item.series;
            if (!filterValue || !filterLabel) {
                return [];
            }
            return [
                {
                    filterValue: filterValue.toString(),
                    filterLabel: filterLabel.toString(),
                    filterPosition
                }
            ];
        },
        filterOrder: 2
    },
    application: {
        type: FILTER_TYPE.SELECT,
        settings: {
            labelTranslation: "camera.filter.industriesandapplications.label",
            emptyOptionLabelTranslation:
                "camera.filter.industriesandapplications.dropdownbutton"
        },
        func: filterFunc,
        itemAccessor: ({ applications = [] }) =>
            applications.map(
                ({ filterValue, filterLabel, filterPosition }) => ({
                    filterValue: filterValue.toString(),
                    filterLabel: filterLabel.toString(),
                    filterPosition
                })
            )
    },
    imageFormat: {
        type: FILTER_TYPE.BTN,
        settings: {
            labelTranslation: "camera.filter.imageformat.label",
            type: "image"
        },
        func: imgFormatFilterFunc,
        itemAccessor: (item) => {
            return item.imageFormat || [];

            // ? Not sure if filter only checks for max image format ?

            // const maxFormat = imageFormats.reduce(
            //     (currentMaxFormat, imageFormat) => {
            //         const prevPixelCount = currentMaxFormat.pixelCount || 0;
            //         const [pWidth, pHeight] =
            //             imageFormat.filterLabel.split(" x ");

            //         const currentPixelCount =
            //             parseInt(pWidth) * parseInt(pHeight);

            //         if (prevPixelCount > currentPixelCount)
            //             return { ...currentMaxFormat };

            //         return { ...imageFormat, pixelCount: currentPixelCount };
            //     },
            //     {}
            // );

            // const { filterValue, filterLabel, filterPosition } = maxFormat;

            // if (!filterValue || !filterLabel) {
            //     return [];
            // }
            // return [
            //     {
            //         filterValue: filterValue.toString(),
            //         filterLabel: filterLabel.toString(),
            //         filterPosition
            //     }
            // ];
        },
        filterOrder: 3
    },
    maxFrequencyPartFrame: {
        type: FILTER_TYPE.RANGE,
        settings: {
            labelTranslation: "camera.filter.max-frequency-part-frame.label"
        },
        func: frequencyFilterFunc,
        itemAccessor: (item) => {
            const { filterValue, filterLabel } = item.maxFrequencyPart ?? {};

            if (!filterValue) {
                return [];
            }

            return [
                {
                    filterValue: filterValue.toString(),
                    filterLabel: filterLabel.toString(),
                    filterPosition: +filterLabel
                }
            ];
        }
    },
    maxFrequencyFullFrame: {
        type: FILTER_TYPE.RANGE,
        settings: {
            labelTranslation: "camera.filter.max-frequency-full-frame.label"
        },
        func: frequencyFilterFunc,
        itemAccessor: (item) => {
            const { filterValue, filterLabel } = item.maxFrequencyFull ?? {};

            if (!filterValue) {
                return [];
            }

            return [
                {
                    filterValue: filterValue.toString(),
                    filterLabel: filterLabel.toString(),
                    filterPosition: +filterLabel
                }
            ];
        },
        filterOrder: 4
    },
    thermalResolution: {
        type: FILTER_TYPE.BTN,
        settings: {
            labelTranslation: "camera.filter.thermalresolution.label",
            type: "thermal"
        },
        func: filterFunc,
        itemAccessor: (item) => {
            const { filterValue, filterLabel } = item.thermalResolution ?? {};
            if (!filterValue) {
                return [];
            }
            return [
                {
                    filterValue: filterValue.toString(),
                    filterLabel: filterLabel.toString()
                }
            ];
        },
        filterOrder: 5
    }
};

const sortedDefinitionKeys = Object.keys(filterDefinitions)
// .sort(
//     (keyA, keyB) =>
//         filterDefinitions[keyA].filterOrder -
//         filterDefinitions[keyB].filterOrder
// );

function buildFilters(items) {
    const init = sortedDefinitionKeys.reduce((carry, key) => {
        const { type, settings, func, itemAccessor } = filterDefinitions[key];
        carry[key] = {
            type,
            settings,
            func: (f, item) => func(f, item, itemAccessor),
            options: []
        };
        return carry;
    }, {});

    // map item values to filter options
    const filterMap = items.reduce((carry, item) => {
        // for each filter
        for (let filterKey of sortedDefinitionKeys) {
            const { itemAccessor } = filterDefinitions[filterKey];

            const itemValues = itemAccessor(item);
            if (!itemValues) {
                continue;
            }

            // for each item value
            for (let itemValue of itemValues) {
                const { filterValue, filterLabel, filterPosition } = itemValue;

                const hasValue =
                    carry[filterKey].options.find(
                        ({ value }) => value === filterValue
                    ) !== undefined;
                if (hasValue) {
                    continue;
                }

                // create filter option from item value
                carry[filterKey].options.push({
                    position: filterPosition,
                    value: filterValue,
                    label: filterLabel,
                    active: false,
                    disabled: false
                });
            }
        }

        return carry;
    }, init);

    const applicationSortDef = sortOptionDefinitions.find(def => def.value === 'application');

    return Object.keys(filterMap).map((key) => {
        const { options, ...filter } = filterMap[key];
        return {
            ...filter,
            key,
            options: options.sort(
                (a, b) => {
                    if (key === 'application' && applicationSortDef) {
                        return applicationSortDef.func(a.label, b.label) * applicationSortDef.direction;
                    }
                    // Existing sorting logic for other filters
                    if (a.position >= 0 && b.position >= 0) {
                        return a.position - b.position;
                    }
                    return a.label.toLowerCase().localeCompare(b.label.toLowerCase());
                }
            )
        };
    });
}

const actions = {
    reset: (initialState, state) => {
        return {
            ...initialState,
            sortBy: state.sortBy
        };
    },
    sort: (initialState, state, { by }) => {
        if (!by) {
            throw new Error("invalid sortAction params");
        }
        const sortOptDef = sortOptionDefinitions.find(
            ({ value }) => value === by
        );
        if (!sortOptDef) {
            throw new Error("invalid sortAction params");
        }

        const { func, itemAccessor, direction } = sortOptDef;

        const sortedItems = [
            ...state.items.sort((a, b) =>
                func(itemAccessor(a), itemAccessor(b), direction)
            )
        ];

        return {
            ...state,
            items: sortedItems,
            sortBy: by
        };
    },
    filter: (initialState, state, params) => {
        if (
            params.key === undefined ||
            params.mode === undefined ||
            !FILTER_MODE.isValid(params.mode)
        ) {
            throw new Error("invalid filterAction params");
        }

        // update filters: add, remove or replace active options
        let newFilters = state.filters.map((filter) => {
            if (filter.key !== params.key) {
                return filter;
            }
            return {
                ...filter,
                options: filter.options.map((opt) => {
                    if (
                        params.value !== undefined &&
                        opt.value !== params.value
                    ) {
                        // this is not the requested option
                        return {
                            ...opt,
                            active:
                                params.mode === FILTER_MODE.REPLACE
                                    ? false
                                    : opt.active
                        }; // set option inactive when using filterMode "replace"
                    }
                    // this is the requested option or no option has been specified
                    return {
                        ...opt,
                        active:
                            params.mode === FILTER_MODE.ADD ||
                            params.mode === FILTER_MODE.REPLACE
                    }; // set option inactive when using filterMode "remove" and active otherwise
                })
            };
        });

        // aggregate active filters
        const active = newFilters
            .map(({ key, options }) => ({
                key,
                options: options.filter(({ active }) => active)
            }))
            .filter(({ options }) => options.length > 0);

        // filter all items
        const result = initialState.items.filter((item) => {
            for (let f of newFilters) {
                if (f.func(f, item) === false) {
                    return false;
                }
            }
            return true;
        });

        // todo: disable filter options that are not part of the result
        const availableFilters = buildFilters(result);

        newFilters = newFilters.map((f) => {
            // options of filters of type "CHECK" don't get disabled if that filter has an active option
            if (
                f.type === FILTER_TYPE.CHECK &&
                f.options.filter((opt) => opt.active).length > 0
            ) {
                return {
                    ...f,
                    options: f.options.map((opt) => ({
                        ...opt,
                        disabled: false
                    }))
                };
            }
            // look for available filters
            const af = availableFilters.find(({ key }) => key === f.key);
            // if there is nothing available, disable all options
            if (!af) {
                return {
                    ...f,
                    options: f.options.map((opt) => ({
                        ...opt,
                        disabled: true
                    }))
                };
            }

            // disable options that aren't available
            return {
                ...f,
                options: f.options.map((opt) => ({
                    ...opt,
                    disabled: !af.options.some(
                        (afOpt) => afOpt.value === opt.value
                    )
                }))
            };
        });

        return {
            ...state,
            filters: newFilters,
            active,
            items: result
        };
    },
    // [{ key, [option]}, { key, [option]}] -> [{key, value}]
    filterMultiple: (initialState, state, { filterList = [] }) => {
        const filters = filterList.reduce(
            (carry, { key, options }) => [
                ...carry,
                ...options.reduce(
                    (prev, { value }) => [
                        ...prev,
                        { mode: FILTER_MODE.ADD, key, value }
                    ],
                    []
                )
            ],
            []
        );

        return filters.reduce(
            (prevState, filter) =>
                actions.filter(initialState, prevState, filter),
            state
        );
    }
};

function useCameraFilter({ allItems = [], itemsPerPage }) {
    const allFilters = useMemo(() => buildFilters(allItems), [allItems]);

    const initialState = useMemo(
        () => ({
            filters: allFilters,
            active: [],
            items: allItems,
            sortBy: sortOptionDefinitions[0].value
        }),
        [allFilters, allItems]
    );

    const [processCount, setProcessCount] = useState(0);

    const addProcess = (currentState) => {
        return currentState + 1;
    };

    const removeProcess = (currentState) => {
        return currentState - 1;
    };

    const sortOptions = useMemo(
        () =>
            sortOptionDefinitions.map(({ value, labelTranslation }) => ({
                labelTranslation,
                value
            })),
        []
    );

    const [state, dispatch] = useReducer(
        (currentState, { action, ...params }) => {
            setProcessCount(addProcess);
            const resultState = action(initialState, currentState, params);

            window.sessionStorage.setItem(
                ACTIVE_FILTERS_KEY,
                JSON.stringify(resultState.active)
            );
            window.sessionStorage.setItem(SORT_BY_KEY, resultState.sortBy);

            setTimeout(() => {
                setProcessCount(removeProcess);
            }, 300);

            return resultState;
        },
        initialState
    );

    useEffect(() => {
        const activeFilters = JSON.parse(
            window.sessionStorage.getItem(ACTIVE_FILTERS_KEY) ?? "[]"
        );

        if (activeFilters != null) {
            dispatch({
                action: actions.filterMultiple,
                filterList: activeFilters ?? []
            });
        }

        const by = window.sessionStorage.getItem(SORT_BY_KEY);

        if (!by) return;

        dispatch({
            action: actions.sort,
            by
        });
    }, []);

    return {
        /*
            [
                {
                    key: "",
                    type: 0,
                    settings: {},
                    options: [
                        {
                            value: "",
                            label: "",
                            active: false,
                            disabled: false
                        }
                    ]
                }
            ]
         */
        filters: state.filters,
        /*
            [
                {
                    key: "",
                    labels: [
                        ""
                    ]
                }
            ]
         */
        active: state.active,
        /*
            [ { whatever structure was supplied } ]
         */
        items: state.items,
        isFiltered:
            state.active.length > 0 ||
            (!!state.sortBy &&
                state.sortBy !== sortOptionDefinitions[0].value),
        resultCount: state.items.length,
        /*
        {
            active: "category",
            options: [
                {
                    value: "category",
                    translationLabel: "translate-key"
                }
            ]
        }
         */
        sortBy: {
            active: state.sortBy,
            options: sortOptions
        },
        isLoading: processCount > 0,
        // reset state
        resetAction: () => dispatch({ action: actions.reset }),
        // sort
        sortAction: (by) => dispatch({ action: actions.sort, by }),
        // deactivte all filter options of filter "key" (use action "filter" but leave value as undefined)
        removeFilterAction: (key) =>
            dispatch({
                action: actions.filter,
                mode: FILTER_MODE.REMOVE,
                key
            }),
        // activate filter option "value" of filter "key"
        addFilterOptionAction: (key, value) =>
            dispatch({
                action: actions.filter,
                mode: FILTER_MODE.ADD,
                key,
                value
            }),
        addMultipleFilterOptionAction: (filterList) =>
            dispatch({
                action: actions.filterMultiple,
                mode: FILTER_MODE.ADD,
                filterList
            }),
        // deactivate filter option "value" of filter "key"
        removeFilterOptionAction: (key, value) =>
            dispatch({
                action: actions.filter,
                mode: FILTER_MODE.REMOVE,
                key,
                value
            }),
        // replace all active filter options of filter "key" with filter option "value"
        replaceFilterOptionAction: (key, value) =>
            dispatch({
                action: actions.filter,
                mode: FILTER_MODE.REPLACE,
                key,
                value
            })
    };
}

export { useCameraFilter, FILTER_TYPE };
